import React, { useState, useEffect, useCallback } from 'react';
import LoadingSpinner from '../components/LoadingSpinner';
import Comments from '../components/Comments';
import '../css.css';

const FishingTypes = () => {
    const [qualificationData, setQualificationData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedType, setSelectedType] = useState(null);

    const fetchQualificationData = useCallback(async () => {
        setLoading(true);
        setError(null);
        
        try {
            const response = await fetch('/api/qualification');
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            setQualificationData(data);
        } catch (error) {
            console.error('Error fetching qualification data:', error);
            setError('자격 정보를 불러오는데 실패했습니다.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchQualificationData();
    }, [fetchQualificationData]);

    // 데이터를 type_id별로 그룹화
    const groupedData = React.useMemo(() => {
        const groups = {};
        qualificationData.forEach(item => {
            if (!groups[item.type_id]) {
                groups[item.type_id] = {
                    name: item.type_name,
                    levels: []
                };
            }
            groups[item.type_id].levels.push(item);
        });
        return groups;
    }, [qualificationData]);

    // 낚시 타입별 대표 이미지 매핑
    const getTypeImage = (typeId) => {
        const imageMap = {
            1: "/data/img/qualification/2516249c94bcb2b408eecb94c32d6c24ef69489b6956148e40c524cdbced32e0.webp", // 클래식 스피닝
            2: "/data/img/qualification/806fca5948d87cb9a583cf29be8a10c587d8cb0069f4eba519adfbbe28053629.webp", // 지깅
            3: "/data/img/qualification/053de81412a39b6fc871c5a3e38be9c50fec143c1af4d7430337d3858e50236e.webp", // 라이트 & 울트라 라이트
            4: "/data/img/qualification/e5da22a359dc320f5ff104256c6c585a86a596c8833059be70da616943d250b1.webp", // 텔레스코픽 낚시
            5: "/data/img/qualification/6e56aa9b7dc60f011ef4a095f2b675d7023d554201d184e7c95a1d5e6cc9e228.webp", // 볼로네제 & 매치 낚시
            6: "/data/img/qualification/5dc5527a606e054065d5ba366a97111fd7f064ac82fd688f4027df1bdbb72137.webp", // 피더
            7: "/data/img/qualification/8726a1c486677fe27f74d4e24d5b7e649d6c3c434ce83a69fddce6eef113030b.webp", // 잉어 낚시
            8: "/data/img/qualification/39f663ec6356418463695a241654565dd0e6616a280b701f53ce3b2852acf91d.webp"  // 해양 낚시
        };
        return imageMap[typeId] || "";
    };

    const handleImageClick = (typeId) => {
        setSelectedType(typeId);
        // details-section으로 스크롤 (네비바 높이만큼 오프셋 적용)
        setTimeout(() => {
            const detailsSection = document.querySelector('.details-section');
            if (detailsSection) {
                const navbarHeight = 80; // 네비게이션 바 높이 (조정 필요시 변경)
                const elementPosition = detailsSection.getBoundingClientRect().top + window.pageYOffset;
                const offsetPosition = elementPosition - navbarHeight;
                
                window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                });
            }
        }, 100); // 약간의 지연을 주어 DOM 업데이트 후 스크롤
    };

    if (loading) {
        return (
            <>
                <h1 className="page-title">자격 정리</h1>
                <div className="spinner-container">
                    <LoadingSpinner
                        height={80}
                        width={80}
                        color="#4fa94d"
                        visible={true}
                    />
                </div>
                <Comments pageName="qualification" />
            </>
        );
    }

    if (error) {
        return (
            <>
                <h1 className="page-title">자격 정리</h1>
                <div className="error-message">
                    <p>{error}</p>
                    <button onClick={fetchQualificationData}>다시 시도</button>
                </div>
                <Comments pageName="qualification" />
            </>
        );
    }

    return (
        <>
            <h1 className="page-title">자격 정리</h1>
            <div className="qualification-container">
                <div className="qualification-grid">
                    {Object.entries(groupedData).map(([typeId, typeData]) => (
                        <div 
                            key={typeId} 
                            className={`qualification-card ${selectedType === parseInt(typeId) ? 'active' : ''}`}
                            onClick={() => handleImageClick(parseInt(typeId))}
                        >
                            <div className="card-image-wrapper">
                                <img
                                    src={getTypeImage(parseInt(typeId))}
                                    alt={typeData.name}
                                />
                                <div className="card-overlay">
                                    <span className="card-title">{typeData.name}</span>
                                    <span className="card-subtitle">{typeData.levels.length}단계</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                
                {selectedType && groupedData[selectedType] && (
                    <div className="details-section">
                        <div className='scroll-gap'></div>
                        <div className="details-header">
                            <h2>{groupedData[selectedType].name}</h2>
                            <button 
                                className="close-btn"
                                onClick={() => setSelectedType(null)}
                            >
                                ✕
                            </button>
                        </div>
                        <div className="qualification-levels">
                            {groupedData[selectedType].levels.map((level, index) => (
                                <div key={level.id} className="level-card">
                                    <div className='level-image-section'>
                                        <img src={`/data/img/qualification/${level.filename}`} alt={`${level.level}단계`} />
                                        <div className="level-badge">{level.level}단계</div>
                                    </div>
                                    <div className='level-content'>
                                        <h3>과제 목록</h3>
                                        <ul className="task-list">
                                            <li>{level.task1}</li>
                                            <li>{level.task2}</li>
                                            <li>{level.task3}</li>
                                        </ul>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
            <Comments pageName="qualification" />
        </>
    );
};

export default FishingTypes;